<template>
  <div class="border-static-default-mid rounded-md mb-16 border p-16 pb-8">
    <div
      class="mb-8 flex flex-col items-baseline justify-between md:flex-row md:items-center"
    >
      <div>
        <div class="flex items-center">
          <Flag class="mr-8" :country-code="market" size="l" />
          <div class="items-baseline">
            <span class="body-1-bold mr-8"> {{ i18n.country(market) }} </span>
            <span class="text-action-default-low caption opacity-60">
              {{ marketStatus }}
            </span>
          </div>
        </div>
        <div class="text-action-default-low body-2 ml-32 mt-4">
          {{ subtitle }}
          <RevLink v-if="!!termsLink" target="_blank" :to="termsLink">
            {{ i18n(translations.termsAndConditions) }}
          </RevLink>
        </div>
      </div>
      <RevButton
        class="mb-8 sm:mt-16"
        size="small"
        variant="secondary"
        @click="handleSelectEprMarket"
      >
        {{ i18n(translations.addDetails) }}
      </RevButton>
    </div>
    <RevTable
      v-if="sortedEprDetails.length"
      class="-mx-16 mt-8"
      :collection="sortedEprDetails"
      :columns
      transparent-header
    >
      <template #body-category="{ item }">
        <span class="body-2-bold">
          {{ getCategoryLabel(item.category) }}
        </span>
      </template>
      <template #body-eprNumber="{ item }">
        {{ item.isExempted ? i18n(translations.exempt) : item.eprNumber }}
      </template>
      <template #[`body-${TABLE_COLUMN_KEYS.DOWNLOAD}`]="{ item }">
        <div
          v-if="item.document"
          class="border-static-default-mid rounded-md flex max-w-[450px] items-center border p-4"
        >
          <IconBill class="flex shrink-0" size="medium" />
          <div class="ml-8 flex shrink truncate">
            <span class="truncate">{{ item.document.filename }}</span>
          </div>
          <RevButtonIcon
            v-if="item.document.link"
            :aria-label="i18n(translations.downloadButton)"
            :icon="IconDownload"
            target="_blank"
            :to="item.document.link.href"
            variant="secondary"
          />
        </div>
      </template>
      <template #[`body-${TABLE_COLUMN_KEYS.ACTION}`]="{ item }">
        <RevButtonIcon
          :icon="IconTrash"
          variant="secondary"
          @click="handleDeleteEprDetail(item.id)"
        />
      </template>
    </RevTable>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type { GetEprInformationItem } from '@backmarket/http-api/src/api-specs-seller-backoffice/seller-profile'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevLink } from '@ds/components/Link'
import { RevTable } from '@ds/components/Table'
import { IconBill } from '@ds/icons/IconBill'
import { IconDownload } from '@ds/icons/IconDownload'
import { IconTrash } from '@ds/icons/IconTrash'

import { EPR_CATEGORIES } from '../../EprDetails.constants'

import {
  MANDATORY_MARKETS,
  TERMS_LINKS_BY_MARKETS,
} from './EprDetailsByMarketList.constants'
import translations from './EprDetailsByMarketList.translations'

const TABLE_COLUMN_KEYS = {
  DOWNLOAD: 'download',
  ACTION: 'action',
}

const props = withDefaults(
  defineProps<{
    market: `${MarketCountryCode}`
    eprDetails?: GetEprInformationItem[]
  }>(),
  { eprDetails: () => [] },
)

const emit = defineEmits(['select-market', 'delete-epr-information'])

const i18n = useI18n()

const columns = [
  { key: 'category', label: i18n(translations.category) },
  { key: 'eprNumber', label: i18n(translations.eprNumber) },
  { key: TABLE_COLUMN_KEYS.DOWNLOAD, label: '' },
  { key: TABLE_COLUMN_KEYS.ACTION, label: '', align: 'end' as const },
]

const marketStatus = computed(() =>
  i18n(
    MANDATORY_MARKETS[props.market]
      ? translations.mandatory
      : translations.optional,
  ),
)

const subtitle = computed(() =>
  i18n(translations[`eprSubTitle${props.market}` as keyof typeof translations]),
)

const termsLink = computed(() => TERMS_LINKS_BY_MARKETS[props.market])

const sortedEprDetails = computed(() =>
  [...props.eprDetails].sort((a, b) => {
    const categoryA = a.category.label?.toUpperCase() || 0
    const categoryB = b.category.label?.toUpperCase() || 0

    return categoryA < categoryB ? -1 : 1
  }),
)

function getCategoryLabel(category: GetEprInformationItem['category']) {
  return category.code === EPR_CATEGORIES.DEEE
    ? i18n(translations.fullDeeeLabel)
    : category.label
}

function handleSelectEprMarket() {
  emit('select-market', props.market)
}

function handleDeleteEprDetail(id: GetEprInformationItem['id']) {
  emit('delete-epr-information', id)
}
</script>
